import { Feature } from "geojson";
import { useCallback, useState } from "react";

import { useGaiaMap } from "../../../hooks/useGaiaMap";
import { useDragCompletion } from "./useDragCompletion";
import { useDragCursor } from "./useDragCursor";
import { useDragInitiation } from "./useDragInitiation";
import { useDragMovement } from "./useDragMovement";
import { useMapInteractionToggle } from "./useMapInteractionToggle";

const initialDragState = {
    isDragging: false,
    draggedFeature: null,
    hoveredFeature: null,
    currentGeoPosition: null,
} as const;

type DragState<T extends Feature = Feature> = {
    isDragging: boolean;
    draggedFeature: T | null;
    hoveredFeature: T | null;
    currentGeoPosition: [number, number] | null;
};

type GaiaMapFeatureDragListenerProps<T extends Feature = Feature> = {
    layerIds: string[];
    isDraggable?: (feature: T) => boolean;
    isDropTarget?: (draggedFeature: T, droppedOnFeature: T) => boolean;
    hoverBoundingBoxSize?: number;
    callbacks?: {
        onDragStart?: (feature: T) => void;
        onDrag?: (feature: T, position: [number, number]) => void;
        onDragEnd?: (feature: T, position: [number, number], droppedOnFeature?: T) => void;
        onDragCancel?: (feature: T) => void;
    };
    children: (state: DragState<T>) => React.ReactNode;
};

export function GaiaMapFeatureDragListener<T extends Feature = Feature>({
    layerIds,
    isDraggable = () => true,
    isDropTarget = () => true,
    hoverBoundingBoxSize = 5,
    callbacks,
    children,
}: GaiaMapFeatureDragListenerProps<T>) {
    const { map } = useGaiaMap();
    const [dragState, setDragState] = useState<DragState<T>>(initialDragState as DragState<T>);

    useDragInitiation({
        map,
        layerIds,
        isDraggable,
        hoverBoundingBoxSize,
        onDragStart: useCallback(
            (feature: T, position: [number, number]) => {
                setDragState({
                    isDragging: true,
                    draggedFeature: feature,
                    hoveredFeature: null,
                    currentGeoPosition: position,
                });
                callbacks?.onDragStart?.(feature);
            },
            [callbacks],
        ),
    });

    useDragMovement({
        map,
        isDragging: dragState.isDragging,
        draggedFeature: dragState.draggedFeature,
        layerIds,
        isDropTarget,
        hoverBoundingBoxSize,
        onDragMove: useCallback(
            (position: [number, number], hoveredFeature: T | null) => {
                setDragState(prev => ({
                    ...prev,
                    currentGeoPosition: position,
                    hoveredFeature,
                }));

                if (dragState.draggedFeature) {
                    callbacks?.onDrag?.(dragState.draggedFeature, position);
                }
            },
            [callbacks, dragState.draggedFeature],
        ),
    });

    useDragCompletion({
        map,
        isDragging: dragState.isDragging,
        onDragEnd: () => {
            const { draggedFeature, currentGeoPosition, hoveredFeature } = dragState;

            if (draggedFeature && currentGeoPosition) {
                callbacks?.onDragEnd?.(draggedFeature, currentGeoPosition, hoveredFeature || undefined);
            }

            setDragState(initialDragState as DragState<T>);
        },
    });

    useMapInteractionToggle({
        map,
        isDragging: dragState.isDragging,
    });

    useDragCursor({
        map,
        isDragging: dragState.isDragging,
        hoveredFeature: dragState.hoveredFeature,
    });

    return children(dragState);
}
